import {
  CustomDimensions,
  getCustomChargeObj,
  getExperienceObj,
  getFeeObj,
  getOptionObj,
  getPromoCode,
} from 'utils/analytics/purchaseHelper';
import { businessToData, experienceToData } from './amplitude';
import { consumer, core, safeToNumber } from '@tock/coretypes';
import { displayInDollars } from '@tock/money';
import { isIOSNative } from 'utils/deviceUtils';
import _ from 'lodash';

export const ga4AddScript = (gaProperty: string) => {
  if (window.gtag) {
    return;
  }

  (function () {
    const ga4 = document.createElement('script');
    ga4.src = `https://www.googletagmanager.com/gtag/js?id=${gaProperty}`;
    // window.ga_debug = { trace: true };
    window.dataLayer = window.dataLayer = window.dataLayer || [];
    document.getElementsByTagName('head')[0].appendChild(ga4);
     
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  })();
};

export const ga4SetupTracking = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gtagWrapper: (...args: any[]) => void,
  tockWebProperty: string,
  customDimensions: CustomDimensions,
  advertisingEnabled: boolean
) => {
  gtagWrapper('js', new Date());
  gtagWrapper('set', { [customDimensions.TOCK_APP]: 'Web' });
  // This must occur before the call to config
  ga4SetAdvertisingFeatures(advertisingEnabled, gtagWrapper);
  gtagWrapper('config', tockWebProperty);
  document.addEventListener('deviceready', () => {
    let appType = 'Web';
    if (isIOSNative()) {
      appType = 'Tock App (iOS)';
    }
    if (window.ReactNative) {
      appType = 'Tock App (React Native)';
    }
    gtagWrapper('set', { [customDimensions.TOCK_APP]: appType });
  });
};

export const ga4SetAdvertisingFeatures = (
  advertisingEnabled: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gtagWrapper: (...args: any[]) => void
) => {
  if (advertisingEnabled) {
    gtagWrapper('set', 'allow_google_signals', true);
  } else {
    gtagWrapper('set', 'allow_google_signals', false);
  }
};

export const ga4SetupTrackingRestaurant = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gtagWrapper: (...args: any[]) => void,
  fastRoutingBetweenContext: boolean,
  restaurantWebProperty: string | null,
  trackedDomains: string[]
): void => {
  if (!fastRoutingBetweenContext && restaurantWebProperty) {
    if (trackedDomains.length > 0) {
      gtagWrapper('config', restaurantWebProperty, {
        groups: 'restaurant',
        linker: {
          domains: trackedDomains,
        },
      });
    } else {
      gtagWrapper('config', restaurantWebProperty, { groups: 'restaurant' });
    }
  }
};

export const ga4SendPurchaseEvent = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gtagWrapper: (...args: any[]) => void,
  purchaseOrder: core.PurchaseOrder,
  purchaseCategory: string,
  business: consumer.BusinessConfig,
  ticketType?: core.TicketType,
  sendToRestaurant?: boolean
) => {
  const optionMap: Dictionary<number> = _.reduce(
    purchaseOrder.optionOrder as core.OptionOrder[],
    (acc: Dictionary<number>, optionOrder: core.OptionOrder) => {
      const optionId = optionOrder.optionId;
      acc[optionId] = (acc[_.toString(safeToNumber(optionId))] || 0) + optionOrder.numPurchased;
      return acc;
    },
    {}
  );

  const purchaseOptions = _.filter(
    _.map(purchaseOrder.option as core.Price[], (option: core.Price) =>
      getOptionObj(option, purchaseCategory, business, optionMap)
    ),
    (option) => option
  );

  const purchaseFees = _.map(purchaseOrder.fee ?? [], (fee) =>
    getFeeObj(fee, purchaseCategory, business)
  );

  const customCharges = _.map(
    purchaseOrder.customChargeOrder as core.CustomChargeOrder[],
    (customCharge: core.CustomChargeOrder) =>
      getCustomChargeObj(customCharge, purchaseCategory, business)
  );

  const items = _.concat(
    [],
    purchaseOptions,
    purchaseFees,
    customCharges,
    getExperienceObj(purchaseOrder, purchaseCategory, business)
  );

  gtagWrapper('event', 'purchase', {
    items,
    ...businessToData(business),
    ...experienceToData(ticketType),
    transaction_id: `Res_${safeToNumber(purchaseOrder.id)}`,
    value: displayInDollars(purchaseOrder.totalPrice),
    coupon: getPromoCode(purchaseOrder),
    currency: business.currencyCode,
    send_to: ['default', ...(sendToRestaurant ? ['restaurant'] : [])],
  });
};

export const ga4SendConversionEvent = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gtagWrapper: (...args: any[]) => void,
  purchaseOrder: core.PurchaseOrder,
  sendToRestaurant?: boolean
) =>
  gtagWrapper('event', 'reservation', {
    ticketType: purchaseOrder.ticketTypeName,
    price: _.toInteger(displayInDollars(purchaseOrder.totalPrice)),
    send_to: ['default', ...(sendToRestaurant ? ['restaurant'] : [])],
  });

// export const ga4TransitionToPage = (
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   gtagWrapper: () => (...args: any[]) => void,
//   tockWebProperty: string | null,
//   restaurantWebProperty: string | null,
//   sendRestaurantData: boolean,
//   sanitizedLocation: string,
//   origin: string
// ): void => {
//   gtagWrapper('set', {
//     page: sanitizedLocation,
//     location: `${origin || ''}${sanitizedLocation}`,
//   });

//   if (sendRestaurantData && restaurantWebProperty) {
//     gtagWrapper('config', restaurantWebProperty, {
//       page_path: sanitizedLocation,
//     });
//   }
// };
